import * as React from 'react'
import styles from './styles.module.css'

const clamp01 = (v: number) => Math.min(1, Math.max(0, v))

const formatPct = (v01: number) => `${Math.round(clamp01(v01) * 100)}%`

type Props = {
  /** 0..1 */
  from: number
  /** 0..1 */
  to: number
  /** animation duration for both bar + number */
  durationMs?: number
  easing?: string

  /** if provided, overrides the animated percentage label */
  label?: string

  className?: string
  trackClassName?: string
  fillClassName?: string
  labelClassName?: string
}

/**
 * CSS keyframes for bar + RAF-driven number for smooth label updates.
 */
export const ProgressBarRange = ({
  from,
  to,
  durationMs = 2000,
  easing = 'linear',
  label,
  className,
  trackClassName,
  fillClassName,
  labelClassName,
}: Props) => {
  const f = clamp01(from)
  const t = clamp01(to)

  const [displayed, setDisplayed] = React.useState(f)

  const rafRef = React.useRef<number | null>(null)
  const startRef = React.useRef<number>(0)

  // restart keyframes whenever inputs change
  const animKey = `${f}-${t}-${durationMs}-${easing}`

  React.useEffect(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current)

    // reset label to start
    setDisplayed(f)

    startRef.current = performance.now()
    const delta = t - f

    const tick = (now: number) => {
      const elapsed = now - startRef.current
      const p = Math.min(elapsed / durationMs, 1)

      const value = f + delta * p
      setDisplayed(clamp01(value))

      if (p < 1) {
        rafRef.current = requestAnimationFrame(tick)
      } else {
        rafRef.current = null
      }
    }

    rafRef.current = requestAnimationFrame(tick)

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [f, t, durationMs])

  return (
    <div className={className}>
      <div className={`${styles.track} ${trackClassName ?? ''}`}>
        <div
          key={animKey}
          className={`${styles.fillKeyframes} ${fillClassName ?? ''}`}
          style={
            {
              ['--from' as any]: `${Math.round(f * 100)}%`,
              ['--to' as any]: `${Math.round(t * 100)}%`,
              ['--dur' as any]: `${durationMs}ms`,
              ['--ease' as any]: easing,
            } as React.CSSProperties
          }
        />
      </div>

      <div className={`${styles.label} ${labelClassName ?? ''}`}>
        {label ?? formatPct(displayed)}
      </div>
    </div>
  )
}
